import type {Blocker, History, Transition} from 'history';
import {ContextType, useCallback, useContext, useEffect} from 'react';
import {Navigator as BaseNavigator, UNSAFE_NavigationContext as NavigationContext} from 'react-router-dom';

// NOTE: This doesn't seem to work for iOS chrome page reloads
export const useBrowserSneezeGuard = (hasUnsavedChanges: boolean) => {
  useEffect(() => {
    const handleBeforeUnload = (e: any) => {
      if (hasUnsavedChanges) {
        e.returnValue = '';
        return ' ';
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);
};



interface Navigator extends BaseNavigator {
    block: History['block'];
}

type NavigationContextWithBlock = ContextType<typeof NavigationContext> & { navigator: Navigator };

/**
 * @source https://github.com/remix-run/react-router/commit/256cad70d3fd4500b1abcfea66f3ee622fb90874
 *
 * DO NOT BUMP react-router-dom PAST 6.2.1 WITHOUT REWRITING THIS HOOK.
 *
 * `navigator.block` was removed in react-router 6.4 (the @remix-run/router
 * rewrite). Any 6.30.x install makes the call below `undefined`, and every
 * screen using the sneeze guard dies with "n.block is not a function".
 * This has now shipped and been reverted four times: PR #561 -> #564,
 * #582, PR #618 -> #621, PR #648 -> this one.
 *
 * The real fix is the data-router `useBlocker` API, which requires converting
 * App.tsx from <BrowserRouter> to createBrowserRouter + <RouterProvider>.
 * Until that happens, the 6.2.1 pin is load-bearing and re-pinning it for
 * CVE-2025-68470 will take the portal down.
 */
export function useBlocker(blocker: Blocker, when = true) {
  const {navigator} = useContext(NavigationContext) as NavigationContextWithBlock;

  useEffect(() => {
    if (!when) {
      return;
    }

    const unblock = navigator.block((tx: Transition) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          // Automatically unblock the transition so it can play all the way
          // through before retrying it. TODO: Figure out how to re-enable
          // this block if the transition is cancelled for some reason.
          unblock();
          tx.retry();
        },
      };

      blocker(autoUnblockingTx);
    });

    return unblock;
  }, [navigator, blocker, when]);
}

/**
 * @source https://github.com/remix-run/react-router/issues/8139#issuecomment-1021457943
 */
export function useInAppSneezeGuard(message: string | ((
    location: Transition['location'],
    action: Transition['action'],
) => string), when = true) {
  const blocker = useCallback((tx: Transition) => {
    let response;
    if (typeof message === 'function') {
      response = message(tx.location, tx.action);
      if (typeof response === 'string') {
        response = window.confirm(response);
      }
    } else {
      response = window.confirm(message);
    }
    if (response) {
      tx.retry();
    }
  }, [message]);
  return useBlocker(blocker, when);
}
